import {
  type ComponentPropsWithoutRef,
  type FunctionComponent,
  forwardRef,
  memo,
} from "react";

import { cn } from "@repo/cn";
import type { SVGIconProps } from "@repo/icons/interface";

interface ICpsTextArea extends ComponentPropsWithoutRef<"textarea"> {
  title?: string;
  helperText?: string;
  containerClassName?: string;
  Icon?: FunctionComponent<SVGIconProps>;
  onIconClick?: () => void;
}

/**
 *  A simple text area.
 *
 * @example
 * ```tsx
 * <CpsTextArea title="Campo de texto" />
 * ```
 * @param title - The title of the text area.
 * @param helperText - The helper text of the text area.
 * @param props - The props of the text area.
 *
 * @returns The `CpsTextArea` component.
 */
export const CpsTextArea = forwardRef<HTMLTextAreaElement, ICpsTextArea>(
  (
    {
      title,
      helperText,
      Icon,
      onIconClick,
      className,
      containerClassName,
      ...props
    },
    ref,
  ) => {
    const cpsTextAreaClasses = cn(
      "mt-1 flex w-full rounded-md border-2 border-neutral-200 bg-white p-4 pr-12 text-sm font-normal text-neutral-600 transition-all duration-200 placeholder:text-neutral-300 disabled:cursor-not-allowed disabled:bg-neutral-50",
      !props.disabled &&
        "hover:border-primary-300 focus:border-primary-400 focus:ring-primary-200 focus:outline-hidden focus:ring-3",
      className,
    );

    return (
      <div className={cn("w-full", containerClassName)}>
        {title ? (
          <div className="flex gap-2 items-center">
            <label className="font-medium">
              {title}
              {props.required ? "*" : null}
            </label>
            {Icon && onIconClick ? (
              <button onClick={onIconClick} tabIndex={0} type="button">
                <Icon className="fill-neutral-300" size={24} />
              </button>
            ) : null}
          </div>
        ) : null}
        <div
          className={cn(
            "relative",
            containerClassName && "flex flex-1 flex-col",
          )}
        >
          <textarea {...props} className={cpsTextAreaClasses} ref={ref} />
          {helperText ? (
            <span className="text-sm font-normal text-neutral-300">
              {helperText}
            </span>
          ) : null}
        </div>
      </div>
    );
  },
);

const MemoCpsTextArea = memo(CpsTextArea);

export default MemoCpsTextArea;
